import * as React from 'react';

import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Container from '@mui/material/Container';
import IconButton from '@mui/material/IconButton';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Stack from '@mui/material/Stack';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import DirectionsCarIcon from '@mui/icons-material/DirectionsCar';
import PersonAddAltIcon from '@mui/icons-material/PersonAddAlt';

import { alpha } from '@mui/material/styles';

import { Navigate, useNavigate } from 'react-router-dom';

import apiClient, { shouldRedirectToLogin } from '../services/api-client';
import AppConfig from '../services/app-config';
import CustomSnackbar from '../components/CustomSnackbar';

export default function ManualVisit({ setNavbarState, navbarState }) {
  const navigate = useNavigate();

  const [redirect, setRedirect] = React.useState({ trigger: false, route: '/' });
  const [loading, setLoading] = React.useState(false);

  const [visitorName, setVisitorName] = React.useState('');
  const [walking, setWalking] = React.useState(true);
  const [vehicle, setVehicle] = React.useState(false);
  const [vehiclePlate, setVehiclePlate] = React.useState('');
  const [unit, setUnit] = React.useState('');
  const [notes, setNotes] = React.useState('');
  const [unitError, setUnitError] = React.useState('');

  const [snackbar, setSnackbar] = React.useState({ open: false, message: '', severity: 'success' });

  const visitorNameHasError = visitorName.trim() !== '' && visitorName.trim().split(/\s+/).length < 2;

  const unitRegex = AppConfig.get('unit_regex');
  const unitSample = AppConfig.get('unit_sample');

  React.useEffect(() => {
    setNavbarState({ ...navbarState, showBack: false, showOptions: false, showSearch: false });
  }, []);

  const handleSnackbarClose = (event, reason) => {
    if (reason === 'clickaway') return;
    setSnackbar({ ...snackbar, open: false });
  };

  const validateUnit = (value) => {
    if (!value.trim()) {
      setUnitError('Ingrese la unidad.');
      return false;
    }
    if (unitRegex) {
      const regex = new RegExp(`^${unitRegex}$`);
      if (!regex.test(value.trim())) {
        setUnitError(`Formato inválido. Ejemplo: ${unitSample || unitRegex}`);
        return false;
      }
    }
    setUnitError('');
    return true;
  };

  const handleCreate = () => {
    if (visitorName.trim() === '') {
      setSnackbar({ open: true, message: 'Ingrese el nombre del visitante.', severity: 'warning' });
      return;
    }
    if (visitorName.trim().split(/\s+/).length < 2) {
      setSnackbar({ open: true, message: 'Por favor agregar al menos un nombre y un apellido.', severity: 'warning' });
      return;
    }
    if (!walking && !vehicle) {
      setSnackbar({ open: true, message: 'Seleccione el tipo de acceso (peatonal o vehicular).', severity: 'warning' });
      return;
    }
    if (vehicle && !vehiclePlate.trim()) {
      setSnackbar({ open: true, message: 'Ingrese la placa del vehículo.', severity: 'warning' });
      return;
    }
    if (!validateUnit(unit)) {
      return;
    }

    const visitorNameCamelCase = visitorName
      .split(' ')
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');

    const data = {
      visitor_name: visitorNameCamelCase,
      walking,
      vehicle,
      vehicle_plate: vehiclePlate.trim(),
      unit: unit.trim(),
      notes: notes.trim() || null,
    };

    setLoading(true);
    apiClient
      .post('visit/manual', data, {}, { action: 'manual_visit' })
      .then((response) => {
        if (response.status === 200) {
          setSnackbar({ open: true, message: 'Visita registrada exitosamente.', severity: 'success' });
          setTimeout(() => {
            navigate(-1);
          }, 1200);
        } else if (response.status === 401) {
          setRedirect({ trigger: true, route: '/login' });
        } else {
          setSnackbar({ open: true, message: response.data?.msg || 'Error al registrar visita.', severity: 'error' });
        }
      })
      .catch((error) => {
        const msg = error?.response?.data?.msg || 'Error al registrar visita, intente de nuevo.';
        setSnackbar({ open: true, message: msg, severity: 'error' });
      })
      .finally(() => setLoading(false));
  };

  if (redirect.trigger) {
    return <Navigate to={redirect.route} replace={true} />;
  }

  return (
    <Container maxWidth="sm" sx={{ pb: 16 }}>
      <CustomSnackbar open={snackbar.open} message={snackbar.message} severity={snackbar.severity} onClose={handleSnackbarClose} />

      <Box
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 2,
          bgcolor: 'background.default',
          pt: 2,
          pb: 1.5,
          borderBottom: '1px solid',
          borderColor: 'divider',
          backdropFilter: 'blur(8px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <IconButton
            onClick={() => navigate(-1)}
            sx={{
              width: 40,
              height: 40,
              ml: -1,
              color: 'text.primary',
              '&:hover': { backgroundColor: (theme) => alpha(theme.palette.text.primary, 0.08) },
            }}
          >
            <ArrowBackIcon sx={{ fontSize: 28 }} />
          </IconButton>
          <Typography sx={{ fontSize: 24, fontWeight: 800, color: 'text.primary', letterSpacing: '-0.02em' }}>Registro Manual</Typography>
        </Box>
      </Box>

      <Box sx={{ px: 0, pt: 3, pb: 6 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ maxWidth: 520, mx: 'auto' }}>
            <Stack spacing={2.5}>
              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Nombre del Visitante
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Ej. Juan Pérez"
                  value={visitorName}
                  onChange={(e) => setVisitorName(e.target.value)}
                  error={visitorNameHasError}
                  helperText={visitorNameHasError ? 'Ingrese al menos un nombre y un apellido' : ''}
                  InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  {AppConfig.get('unit_type') || 'Unidad'}
                </Typography>
                <TextField
                  fullWidth
                  placeholder={unitSample ? `Ej. ${unitSample}` : 'Ingrese la unidad'}
                  value={unit}
                  onChange={(e) => {
                    setUnit(e.target.value);
                    if (unitError) validateUnit(e.target.value);
                  }}
                  error={!!unitError}
                  helperText={unitError}
                  InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                />
              </Box>

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Tipo de Acceso
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
                  <Button
                    variant={walking ? 'contained' : 'outlined'}
                    color={walking ? 'primary' : 'inherit'}
                    onClick={() => {
                      setWalking(true);
                      setVehicle(false);
                    }}
                    sx={{
                      height: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      borderWidth: 2,
                      fontWeight: 900,
                    }}
                  >
                    <DirectionsWalkIcon sx={{ fontSize: 28 }} />
                    Peatonal
                  </Button>

                  <Button
                    variant={vehicle ? 'contained' : 'outlined'}
                    color={vehicle ? 'primary' : 'inherit'}
                    onClick={() => {
                      setVehicle(true);
                      setWalking(false);
                    }}
                    sx={{
                      height: 92,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 0.75,
                      borderWidth: 2,
                      fontWeight: 900,
                    }}
                  >
                    <DirectionsCarIcon sx={{ fontSize: 28 }} />
                    Vehicular
                  </Button>
                </Box>
              </Box>

              {vehicle && (
                <Box>
                  <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                    Placa del Vehículo
                  </Typography>
                  <TextField
                    fullWidth
                    value={vehiclePlate}
                    onChange={(e) => setVehiclePlate(e.target.value.toUpperCase())}
                    InputProps={{ sx: { height: 56, fontWeight: 600 } }}
                  />
                </Box>
              )}

              <Box>
                <Typography sx={{ fontWeight: 800, fontSize: 12, letterSpacing: '0.06em', ml: 0.5, mb: 0.75, color: 'text.secondary' }}>
                  Notas Adicionales
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={3}
                  placeholder="Observaciones..."
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                />
              </Box>
            </Stack>
          </Box>
        )}
      </Box>

      <Box
        sx={{
          position: 'fixed',
          bottom: 88,
          left: 'calc(var(--app-drawer-width, 0px) + (100vw - var(--app-drawer-width, 0px)) / 2)',
          transform: 'translateX(-50%)',
          width: '100%',
          maxWidth: 600,
          px: 2,
          zIndex: 10,
          pointerEvents: 'none',
        }}
      >
        <Button
          variant="contained"
          color="primary"
          fullWidth
          size="large"
          startIcon={<PersonAddAltIcon />}
          onClick={handleCreate}
          disabled={loading}
          sx={{ height: 56, fontWeight: 900, pointerEvents: 'auto' }}
        >
          Registrar Visita Manual
        </Button>
      </Box>
    </Container>
  );
}
